import React, { useEffect, useState } from "react";
import { Page, FormSectionCard } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";
import _ from "lodash";
import PageListHeader from "../../components/pageListHeader";
import { TextInput, Select, Text, Stack, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { showNotification } from "@mantine/notifications";
import { IconCircleCheck, IconExclamationMark } from "@tabler/icons";
import { useTabNavigate } from "../../hooks/useTabNavigate";

const defaultHsbcMriValues = {
  paymentSetCode: "",
  debitAccount: "",
  currency: "HKD",
  autoplanCode: "F",
  firstPartyReference: "PAYROLL",
};

const currencyOptions = [
  { value: "HKD", label: "HKD" },
  { value: "USD", label: "USD" },
  { value: "CNY", label: "CNY" },
];

const autoplanOptions = [
  { value: "F", label: "F" },
  { value: "A", label: "A" },
];

const HsbcMriSetting = () => {
  const [api] = useServerApi();
  const { t } = useTranslation();
  const tabNav = useTabNavigate();
  const [settingId, setSettingId] = useState(null);
  const [hsbcMri, setHsbcMri] = useState(defaultHsbcMriValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const setField = (field, value) => {
    setHsbcMri((prev) => ({ ...prev, [field]: value }));
  };

  const fetchSetting = async () => {
    try {
      setLoading(true);
      const result = await api.search({
        apiEntity: "setting",
        pageSize: 1,
      });

      if (!result || _.isEmpty(result.docs)) {
        setSettingId(null);
        setHsbcMri({ ...defaultHsbcMriValues });
        return;
      }

      const setting = result.docs[0];
      setSettingId(setting._id);
      setHsbcMri({
        ...defaultHsbcMriValues,
        ...(setting.hsbcMri || {}),
      });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSetting();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      let result;

      if (settingId) {
        const existing = await api.getById({
          apiEntity: "setting",
          id: settingId,
        });
        result = await api.updateById({
          apiEntity: "setting",
          id: settingId,
          values: {
            ...existing,
            hsbcMri,
          },
        });
      } else {
        result = await api.add({
          apiEntity: "setting",
          values: { hsbcMri },
        });
        if (result?.success && result?.data?._id) {
          setSettingId(result.data._id);
        }
      }

      if (result?.success) {
        showNotification({
          title: t("Save"),
          color: "green",
          icon: <IconCircleCheck size={18} />,
          message: t("settings.saved"),
        });
        return;
      }

      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: api.getErrorMsg(result?.error) || t("settings.saveFailed"),
      });
    } catch (error) {
      console.log(error);
      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: t("settings.saveFailed"),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <PageListHeader
        title={t("settings.hsbcMri.title")}
        actionButtons={[
          {
            label: t("Save"),
            onClick: handleSave,
            loading: saving,
            disabled: loading,
          },
          {
            label: t("Close"),
            onClick: () => tabNav.closeActive(),
            variant: "default",
          },
        ]}
      />

      {!loading && (
        <Stack spacing="md">
          <FormSectionCard>
            <Title order={5}>{t("settings.hsbcMri.bankTransfer")}</Title>
            <Text size="xs" color="dimmed" mb="md">
              {t("settings.hsbcMri.description")}
            </Text>
            <TextInput
              label={t("settings.hsbcMri.paymentSetCode")}
              value={hsbcMri.paymentSetCode}
              onChange={(e) =>
                setField("paymentSetCode", e.currentTarget.value)
              }
              placeholder="E01"
              required
              mb="md"
            />
            <TextInput
              label={t("settings.hsbcMri.debitAccount")}
              value={hsbcMri.debitAccount}
              onChange={(e) => setField("debitAccount", e.currentTarget.value)}
              placeholder="001000016001"
              required
              mb="md"
            />
            <Select
              label={t("settings.hsbcMri.currency")}
              data={currencyOptions}
              value={hsbcMri.currency}
              onChange={(value) => setField("currency", value || "HKD")}
              required
              mb="md"
            />
            <Select
              label={t("settings.hsbcMri.autoplanCode")}
              data={autoplanOptions}
              value={hsbcMri.autoplanCode}
              onChange={(value) => setField("autoplanCode", value || "F")}
              mb="md"
            />
            <TextInput
              label={t("settings.hsbcMri.firstPartyReference")}
              value={hsbcMri.firstPartyReference}
              onChange={(e) =>
                setField("firstPartyReference", e.currentTarget.value)
              }
              placeholder="PAYROLL"
            />
          </FormSectionCard>
        </Stack>
      )}
    </Page>
  );
};

export default HsbcMriSetting;
